import { Injectable, inject } from '@angular/core';
import { LatLng } from '../../models/latlng';
import { Range } from '../../models/range';
import { Lane, Route, RouteEVSpeedLimitSegment, RoutePlanElement, RoutePlanType, RoutePoint, Turn } from '../../models/route';
import { WindElement } from '../../models/wind';
import { WaypointService } from '../core/waypoint.service';
import { MapService } from '../map/map.service';
import { UtilsService } from '../utils/utils.service';
import { ECarType } from '../../models/ecar-type';
import { ChargingOperator } from '../../models/charging-operator';
import { AccountService, SubscriptionType } from '../account/account.service';
import { VehicleService } from '../vehicle/vehicle.service';

@Injectable({
  providedIn: 'root'
})
export class DataParserService {

  private readonly utilsService = inject(UtilsService);
  private readonly mapService = inject(MapService);
  private readonly waypointService = inject(WaypointService);
  private readonly accountService = inject(AccountService);
  private readonly vehicleService = inject(VehicleService);

  public parseRangeData(resp): Range {
    const rangeResponse: Range = {
      Range1: resp.range1,
      Range2: resp.range2,
      Temperature: resp.temperature,
      WeatherCode: resp.weathercode,
      Wind: resp.wind.map((windEl) => {
        let windElement: WindElement = {
          Location: new LatLng(windEl[0][0], windEl[0][1]),
          Angle: windEl[1],
          Speed: windEl[2],
          Opacity: windEl[3]
        }
        return windElement;
      })
    };

    return rangeResponse;
  }

  public parseRouteData(resp: any): Route {
    resp.route = this.utilsService.CalculateDistanceBetweenRoutePoints(resp.route);
    resp.route = this.utilsService.CalculateTimeBetweenRoutePoints(resp.route, resp.routeSegments, resp.chargeplan);

    /** Parse route data */
    const routeData: Route = {
      RoutePlan: this.getRoutePlan(resp),
      RoutePoints: resp.route.map((routeEl) => {
        const routePoint: RoutePoint = {
          Location: new LatLng(routeEl[0], routeEl[1]),
          Soc: 1 - routeEl[2],
          RouteSpeedLimit: routeEl[3],
          EVSpeedLimit: routeEl[4],
          Altitude: routeEl[5],
          JamFactor: routeEl[6],
          DistanceFromPreviousPoint: routeEl[7],
          TimeFromPreviousPoint: routeEl[8]
        };

        return routePoint;
      }),
      RouteInfo: {
        ChargeTime: resp.routeData.chargetime,
        Distance: resp.routeData.length,
        ChargingStops: resp.routeData.stops,
        TotalTime: this.calculateTotalTime(resp.route),
        TotalConsumption: null
      },
      RouteEVSpeedLimitSegments: resp.routeSegments.map((routeSegment) => {
        const evSpeedLimitSegment: RouteEVSpeedLimitSegment = {
          SpeedLimit: routeSegment[0],
          StartRouteIndex: routeSegment[1],
          EndRouteIndex: routeSegment[2],
          ShowInfoAtRouteIndex: routeSegment[3]
        }

        return evSpeedLimitSegment;
      }),
      Temperature: resp.temperature,
      Turns: this.getTurns(resp),
      WaypointsAddresses: resp.waypointsInfo,
      WeatherCode: resp.weathercode,
      Wind: resp.wind.map((windEl) => {
        let windElement: WindElement = {
          Location: new LatLng(windEl[0][0], windEl[0][1]),
          Angle: windEl[1],
          Speed: windEl[2],
          Opacity: windEl[3]
        }
        return windElement;
      }),
      Incidents: resp.incidents.map((incident: any) => {
        return {
          Location: new LatLng(incident.lat, incident.lon),
          Type: incident.type,
          Severity: incident.severity
        };
      }),
      RoadSurfaceAlerts: resp.roadsurfacealerts.map((alert: any) => {
        return {
          Location: new LatLng(alert.lat, alert.lon),
          Type: alert.type,
          Severity: alert.severity
        };
      })
    };

    routeData.RouteInfo.TotalConsumption = this.calculateTotalConsumption(routeData);

    return routeData;
  }

  private calculateTotalTime(route: any): number {
    let totalTime: number = 0;
    for (const routePoint of route) {
      totalTime += routePoint[8];
    }
    return Math.round(totalTime);
  }

  private calculateTotalConsumption(routeData: Route): number {
    let actPercent = routeData.RoutePlan[0].BatteryLevel;
    let sumPercent = 0;
    for (let i = 1; i < routeData.RoutePlan.length; i++) {
      if (routeData.RoutePlan[i].Type == RoutePlanType.WayPoint) {
        sumPercent += actPercent - routeData.RoutePlan[i].BatteryLevel;
        actPercent = routeData.RoutePlan[i].BatteryLevel;
      }
      if (routeData.RoutePlan[i].Type == RoutePlanType.Charger || routeData.RoutePlan[i].Type == RoutePlanType.ChargerEndPoint) {
        sumPercent += actPercent - routeData.RoutePlan[i].BatteryMinPercent!;
        actPercent = routeData.RoutePlan[i].BatteryMaxPercent!;
      }
      if (routeData.RoutePlan[i].Type == RoutePlanType.EndPoint) {
        sumPercent += actPercent - routeData.RoutePlan[i].BatteryLevel;
      }
    }

    return Math.round(sumPercent / 100 * this.vehicleService.getSelectedCar().DesignCapacity * this.vehicleService.getBatteryStateOfHealth() / 100);
  }

  private getRoutePlan(resp: any): RoutePlanElement[] {
    const routePlan: RoutePlanElement[] = [];

    /** Start Point */
    routePlan.push(this.getStartPoint(resp));

    let chargeplanIndex: number = 0;
    let waypointIndex: number = 1;
    let waypointsParams = this.waypointService.getWaypointsParams();
    let endpointCharger: boolean = false;

    for (const turn of resp.turns) {
      /** Charger */
      if (turn[0] == 100 || turn[0] == 106) {
        const destination = waypointsParams[waypointsParams.length - 1];
        const charger = this.getCharger(resp, turn, chargeplanIndex, destination, routePlan.at(-1)!);
        if (charger.Type == RoutePlanType.ChargerEndPoint) {
          endpointCharger = true;
        }
        chargeplanIndex++;

        routePlan.push(charger);
      }
      /** Way Point */
      if (turn[0] == 103) {
        routePlan.push(this.getWaypoint(resp, turn, waypointIndex, routePlan.at(-1)!));

        waypointIndex++;
      }
    }

    /** End Point */
    if (!endpointCharger) {
      routePlan.push(this.getEndPoint(resp, routePlan.at(-1)!));
    }

    console.log(routePlan);
    return routePlan;
  }

  private getStartPoint(resp: any): RoutePlanElement {
    return {
      Type: RoutePlanType.StartPoint,
      Name: this.mapService.parseAddressString(resp.waypointsInfo[0], resp.route[0][0], resp.route[0][1]),
      Icon: 'start_icon_list.svg',
      Location: new LatLng(resp.route[0][0], resp.route[0][1]),
      DistanceFromStart: null,
      DistanceFromPreviousElement: null,
      BatteryLevel: this.getBatteryLevel(resp.route[0]),
      BatteryReservedWarning: false,
      WaypointIndex: null,
      BatteryMinPercent: null,
      BatteryMaxPercent: null,
      ChargeTimeHour: null,
      ChargeTimeMin: null,
      OperatorName: null,
      OperatorId: null,
      ChargerIndex: null,
      ChargePower: null,
      ChargingStationId: null,
      WeatherImg: this.getWeatherImg(resp)
    };
  }

  private getCharger(resp: any, turn: any[], chargeplanIndex: number, destination: any, previous: RoutePlanElement): RoutePlanElement {
    const routePlanType = this.isDestinationCharger(resp, chargeplanIndex, destination) ? RoutePlanType.ChargerEndPoint : RoutePlanType.Charger;
    const station = resp.chargingstations[chargeplanIndex];
    const chargeplan = resp.chargeplan[chargeplanIndex];

    let chargeTimeMin: number = Math.round(chargeplan[2] / 5) * 5;

    const chargeTimeHour = Math.floor(chargeTimeMin / 60);
    chargeTimeMin = Math.floor(chargeTimeMin % 60);

    return {
      Type: routePlanType,
      Name: station[4].title,
      Icon: this.utilsService.chargingstationsImg[chargeplan[4] - 1][0],
      Location: new LatLng(station[0], station[1]),
      DistanceFromStart: chargeplan[3] / 1000,
      DistanceFromPreviousElement: chargeplan[3] / 1000 - previous.DistanceFromStart!,
      BatteryReservedWarning: chargeplan[0] < this.getBatterySafetyLimit(),
      BatteryMinPercent: chargeplan[0],
      BatteryMaxPercent: chargeplan[1],
      ChargeTimeHour: chargeTimeHour,
      ChargeTimeMin: chargeTimeMin,
      OperatorName: station[4].opid == -1 ? "Unknown operator" : (this.mapService.getChargingOperatorByID(station[4].opid)?.name ?? null),
      OperatorId: station[4].opid,
      ChargingStationId: station[4].id,
      ChargerIndex: chargeplanIndex,
      ChargePower: station[2],
      BatteryLevel: this.getBatteryLevel(resp.route[turn[4]]),
      WaypointIndex: null,
      WeatherImg: this.getWeatherImg(station[4])
    };
  }

  private isDestinationCharger(resp: any, chargeplanIndex: number, destination: any): boolean {
    const station = resp.chargingstations[chargeplanIndex];
    // Match by csid when the destination was picked as a charging station: its stored
    // coordinates do not always equal the station's coordinates in the response exactly.
    const isLastCharger = chargeplanIndex == resp.chargingstations.length - 1;
    const isDestinationCsid = isLastCharger && destination.csid != null &&
      String(destination.csid) == String(station[4].id);

    return isDestinationCsid || (station[0] == destination.lat && station[1] == destination.lng);
  }

  private getWaypoint(resp: any, turn: any[], waypointIndex: number, previous: RoutePlanElement): RoutePlanElement {
    const routePoint = resp.route[turn[4]];
    return {
      Type: RoutePlanType.WayPoint,
      Name: this.mapService.parseAddressString(resp.waypointsInfo[waypointIndex], routePoint[0], routePoint[1]),
      Icon: null,
      Location: new LatLng(routePoint[0], routePoint[1]),
      DistanceFromStart: turn[3] / 1000,
      DistanceFromPreviousElement: turn[3] / 1000 - previous.DistanceFromStart!,
      BatteryReservedWarning: this.getBatteryLevel(routePoint) < this.getBatterySafetyLimit(),
      BatteryLevel: this.getBatteryLevel(routePoint),
      WaypointIndex: waypointIndex,
      BatteryMinPercent: null,
      BatteryMaxPercent: null,
      ChargeTimeHour: null,
      ChargeTimeMin: null,
      OperatorName: null,
      OperatorId: null,
      ChargerIndex: null,
      ChargePower: null,
      ChargingStationId: null,
      WeatherImg: this.getWeatherImg(resp.waypointsInfo[waypointIndex])
    };
  }

  private getEndPoint(resp: any, previous: RoutePlanElement): RoutePlanElement {
    const routePoint = resp.route[resp.route.length - 1];
    const lastTurn = resp.turns[resp.turns.length - 1];
    const destinationInfo = resp.waypointsInfo[resp.waypointsInfo.length - 1];
    return {
      Type: RoutePlanType.EndPoint,
      Name: this.mapService.parseAddressString(destinationInfo, routePoint[0], routePoint[1]),
      Icon: 'target_icon_blue.svg',
      Location: new LatLng(routePoint[0], routePoint[1]),
      DistanceFromStart: lastTurn[3] / 1000,
      DistanceFromPreviousElement: lastTurn[3] / 1000 - previous.DistanceFromStart!,
      BatteryLevel: this.getBatteryLevel(routePoint),
      BatteryReservedWarning: this.getBatteryLevel(routePoint) < this.getBatterySafetyLimit(),
      WaypointIndex: null,
      BatteryMinPercent: null,
      BatteryMaxPercent: null,
      ChargeTimeHour: null,
      ChargeTimeMin: null,
      OperatorName: null,
      OperatorId: null,
      ChargerIndex: null,
      ChargePower: null,
      ChargingStationId: null,
      WeatherImg: this.getWeatherImg(destinationInfo)
    };
  }

  /** Battery percentage left at a route point row ([lat, lon, socUsed, ...]). */
  private getBatteryLevel(routePoint: any[]): number {
    return 100 - Math.round(routePoint[2] * 100);
  }

  private getBatterySafetyLimit(): number {
    return this.vehicleService.getECarSettings()?.batterySafetyLimitRoute ?? 0;
  }

  /** Weather icon for the `weathercode` of a response element; the free tier gets none. */
  private getWeatherImg(source: { weathercode: number }): string | null {
    return this.accountService.getUserSubscriptionType() == SubscriptionType.Free ? null :
      (this.utilsService.getWeatherImgById(source.weathercode) ?? null);
  }

  private getTurns(resp: any): Turn[] {
    let turns: Turn[] = resp.turns.map((turnEl, index) => {
      const turn: Turn = {
        Idx: index,
        TypeIndex: turnEl[0],
        Id: this.utilsService.turnList[turnEl[0]].id,
        Instruction: [],
        Icon: [],
        CombinedTurn: !(turnEl[0] < 12 || turnEl[0] >= 93),
        RoutePointIdxStart: turnEl[1],
        RoundaboutExitNumber: turnEl[2],
        DistanceFromStart: turnEl[3],
        RoutePointIdxEnd: turnEl[4],
        IsLeftHandTraffic: turnEl[5],
        LaneInfo: this.getLaneInfo(turnEl[6])
      }

      return turn;
    });

    // filter enter roundabout
    turns = turns.filter((el) => {
      return (el.TypeIndex != 94);
    });

    return turns;
  }

  private getLaneInfo(laneData: any[]): Lane[] {
    return laneData.map((laneEl) => {
      let roadLane: Lane = { Highlighted: laneEl[0], Images: [] };

      let orderedLaneIcons: number[] = [];
      for (const laneIcon of laneEl[1]) {
        for (let j = 0; j < this.utilsService.laneTable.length; j++) {
          if (laneIcon == this.utilsService.laneTable[j]) {
            orderedLaneIcons.push(j);
            break;
          }
        }
      }

      // laneTable positions (0-8, single digits), so numeric order equals the former default string sort
      orderedLaneIcons.sort((a, b) => a - b);
      orderedLaneIcons = orderedLaneIcons.map((laneIdx) => { return this.utilsService.laneTable[laneIdx] });
      orderedLaneIcons.forEach((roadLaneIcon, i) => {
        roadLane.Images.push(this.utilsService.laneList[roadLaneIcon].toLocaleLowerCase() + "_" + (i + 1) + ".png");
      });

      return roadLane;
    });
  }

  public parseVehiclesArray(vehiclesData): ECarType[] {
    vehiclesData.sort((a, b) => { return a.vehicle_order - b.vehicle_order });
    const ECars: ECarType[] = [];
    for (const vehicle of vehiclesData) {
      // selecting active cars
      if (vehicle.is_active == 1) {
        ECars.push(new ECarType({
          id: vehicle.id, carWeight: vehicle.car_weight, chargerTypes: vehicle.chargertypes, designCapacity: vehicle.designcapacity,
          dragCoefficient: vehicle.drag_coefficient, dragCross: vehicle.drag_cross, name: vehicle.name, range: vehicle.range,
          factoryRange: vehicle.factory_range, factoryRangeSource: vehicle.range_source, topSpeed: vehicle.topspeed,
          totalPower: vehicle.totalpower, icon: vehicle.image, chargePower: Number.parseFloat(vehicle.chargepower),
          fastChargePower: vehicle.fastchargepower, type: vehicle.type, subtype: vehicle.subtype, greenCarsLink: vehicle.gcc_link
        }));
      }
    }
    return ECars;
  }

  public parseChargingOperatorsArray(operatorsData): ChargingOperator[] {
    return operatorsData.sort((a, b) => { return a.name.localeCompare(b.name) }).map((operatorEl: any) => {
      return {
        name: operatorEl.name,
        id: Number.parseInt(operatorEl.id),
        url: operatorEl.url
      }
    });
  }
}
